import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import analytics from '@xto10x/core/lib/analytics';
import { DeepLinkPaths } from './constants'; // Import DeepLinkPaths
import { shouldTrackPath } from './trackingRules';
import { getCuesFeatureConfig } from '../store/AppConfig/reducer';

/**
 * Generates a CSS selector path for an element (useful for analytics tracking).
 */
const getSelectorPath = (el, maxDepth = 5) => {
  const path = [];
  let depth = 0;
  while (el.parentElement && depth < maxDepth) {
    let selector = el.tagName.toLowerCase();
    if (el.id) {
      selector += `#${el.id}`;
      path.unshift(selector);
      break; // IDs are unique, stop further traversal
    } else if (el.className) {
      selector += `.${Array.from(el.classList).join('.')}`;
    }
    path.unshift(selector);
    /* eslint-disable no-param-reassign */
    el = el.parentElement;
    depth++;
  }
  return path.join(' > ');
};

/**
 * Extracts all `data-*` attributes from an element.
 */
const getDataAttributes = el => {
  const dataAttrs = {};
  Array.from(el.attributes).forEach(attr => {
    if (attr.name.startsWith('data-')) {
      dataAttrs[attr.name] = attr.value;
    }
  });
  return dataAttrs;
};

/**
 * Gets the bounding box (position & size) of an element without triggering reflow.
 */
const getElementBoundingBox = el => {
  const rect = el.getBoundingClientRect();
  return {
    x: Math.round(rect.left),
    y: Math.round(rect.top),
    width: Math.round(rect.width),
    height: Math.round(rect.height)
  };
};

const getAllExtraEventProps = (target, event) => {
  // Guard against null/undefined target
  if (!target) {
    return {
      error: 'Invalid target element'
    };
  }

  // Safer timeOnPage calculation using newer Performance API
  const getTimeOnPage = () => {
    try {
      if (window.performance.timeOrigin) {
        return Math.round((Date.now() - window.performance.timeOrigin) / 1000);
      }
      // Fallback for older browsers
      return Math.round(performance.now() / 1000);
    } catch (e) {
      return null;
    }
  };

  try {
    const pathParts = window.location.pathname.split('/');
    const modulePath = pathParts[2] || '';
    const module = Object.keys(DeepLinkPaths).find(key =>
      modulePath.includes(DeepLinkPaths[key])
    );

    return {
      tag: target.tagName?.toLowerCase() || null,
      action: 'untracked',
      id: target.id || null,
      classList: Array.from(target.classList || []).join(' '),
      text: target.textContent?.trim().substring(0, 50) || null,
      selector: getSelectorPath(target),
      boundingBox: getElementBoundingBox(target),
      dataAttributes: getDataAttributes(target),
      page: window.location?.href,
      viewport: {
        width: window.innerWidth || document.documentElement.clientWidth,
        height: window.innerHeight || document.documentElement.clientHeight
      },
      mousePosition: event ? { x: event.clientX, y: event.clientY } : null,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent || null,
      referrer: document.referrer || null,
      screenResolution: window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : null,
      language: navigator.language || null,
      timeOnPage: getTimeOnPage(),
      module: module || 'unknown'
    };
  } catch (error) {
    // Fallback object if something goes wrong
    return {
      error: error.message,
      tag: target.tagName?.toLowerCase() || null,
      timestamp: new Date().toISOString()
    };
  }
};

const GlobalTracker = () => {
  const cuesFeatureConfig = useSelector(getCuesFeatureConfig);
  useEffect(() => {
    const handleGlobalClick = event => {
      const { target } = event;
      if (!target) return;

      const trackableEl = target.closest('[data-track-event]');
      const hasTrackingAttr = Boolean(trackableEl);

      // do not track if path not in config and no tracking attribute
      if (
        !hasTrackingAttr &&
        !shouldTrackPath(window.location.pathname, cuesFeatureConfig)
      ) {
        return;
      }

      const eventName =
        trackableEl?.getAttribute('data-track-event') || 'untracked_click';
      let eventProps = {};

      if (trackableEl) {
        // Extract event properties from data attributes
        // Get all data-track-* attributes
        const trackAttributes = Array.from(trackableEl.attributes)
          .filter(attr => attr.name.startsWith('data-track-'))
          .reduce(
            (acc, attr) => ({
              ...acc,
              [attr.name.replace('data-track-', '')]: attr.value
            }),
            {}
          );

        // Parse data-track-props if it exists
        let parsedProps = {};
        if (trackAttributes.props) {
          try {
            parsedProps = JSON.parse(trackAttributes.props);
          } catch (err) {
            console.error('Failed to parse data-track-props:', err);
          }
        }

        // Combine all props
        eventProps = {
          ...getAllExtraEventProps(trackableEl, event),
          ...trackAttributes,
          ...parsedProps
        };
      } else {
        // Capture additional metadata if no tracking attribute is found
        eventProps = {
          ...getAllExtraEventProps(target, event)
        };
      }
      // Send event to analytics service (Pendo-like tracking)
      analytics.trackEvent(
        eventName,
        eventProps.action || eventProps.cta,
        null,
        null,
        eventProps
      );
    };

    // Attach click event listener
    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [cuesFeatureConfig]);

  return null;
};

export default GlobalTracker;
